import React, { useEffect, useState, useCallback, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import {
  getStoreList, createStore, updateStore, deleteStore,
  getStoreTables, createStoreTable, updateStoreTable, deleteStoreTable,
  getExcludedProducts, excludeProduct, includeProduct,
  getExcludedOptions, excludeOption, includeOption,
  getStoreReviewIncentive, saveStoreReviewIncentive,
} from '../../api/store';
import { getProductList, getProduct } from '../../api/product';
import { getBrandList } from '../../api/brand';

// 주문 정산 방식 - 매장 목록에 기본값(선결제)이 아닌
// 경우만 짧게 표시해준다.
const SETTLEMENT_MODE_LABEL = { PREPAID: '선결제', POSTPAID: '후결제', ORDER_ONLY: '주문전용' };

export default function StoreManagePage() {
  const { auth } = useAuth();
  // 매장 신규 개설/삭제는 서버(StoreController)에서 SUPER_ADMIN/ADMIN 전용으로 막혀 있다.
  // 지금까지는 STORE_ADMIN 에게도 "+ 추가"/"삭제" 버튼이 그대로 보여서 눌러도 403 에러
  // 얼럿만 뜨는 문제가 있었다 - 역할 경계에 맞게 버튼 자체를 숨긴다(역할별 기능 정리).
  const isStoreAdmin = auth?.roleCode === 'STORE_ADMIN';
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStore, setSelectedStore] = useState(null);

  const [tables, setTables] = useState([]);
  const [loadingTables, setLoadingTables] = useState(false);

  const [storeModal, setStoreModal] = useState(null); // 'new' | storeObj
  const [deleteStoreTarget, setDeleteStoreTarget] = useState(null);
  const [tableModal, setTableModal] = useState(null); // 'new' | tableObj
  const [deleteTableTarget, setDeleteTableTarget] = useState(null);
  const [copiedTableId, setCopiedTableId] = useState(null);
  const [qrDetailTable, setQrDetailTable] = useState(null); // QR 상세보기 모달 대상 테이블
  const [productModalStore, setProductModalStore] = useState(null); // 취급상품 관리 모달 대상 매장
  const [reviewIncentiveModalStore, setReviewIncentiveModalStore] = useState(null); // 리뷰 유도 설정 모달 대상 매장

  const fetchStores = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getStoreList();
      setStores(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || '매장 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStores(); }, [fetchStores]);

  const fetchTables = useCallback(async (storeId) => {
    if (!storeId) { setTables([]); return; }
    setLoadingTables(true);
    try {
      const res = await getStoreTables(storeId);
      setTables(res.data || []);
    } catch {
      setTables([]);
    } finally {
      setLoadingTables(false);
    }
  }, []);

  useEffect(() => { fetchTables(selectedStore?.storeId); }, [selectedStore, fetchTables]);

  const handleSaveStore = async (form) => {
    try {
      if (storeModal && storeModal !== 'new') {
        await updateStore(storeModal.storeId, { ...form, updatedBy: auth.adminName });
      } else {
        await createStore({ ...form, createdBy: auth.adminName });
      }
      setStoreModal(null);
      fetchStores();
    } catch (err) {
      window.alert(err.response?.data?.message || '저장에 실패했습니다.');
    }
  };

  const handleDeleteStore = async () => {
    try {
      await deleteStore(deleteStoreTarget.storeId);
      if (selectedStore?.storeId === deleteStoreTarget.storeId) setSelectedStore(null);
      setDeleteStoreTarget(null);
      fetchStores();
    } catch (err) {
      window.alert(err.response?.data?.message || '삭제에 실패했습니다.');
    }
  };

  const handleSaveTable = async (form) => {
    try {
      if (tableModal && tableModal !== 'new') {
        await updateStoreTable(tableModal.tableId, { ...form, updatedBy: auth.adminName });
      } else {
        await createStoreTable(selectedStore.storeId, { ...form, createdBy: auth.adminName });
      }
      setTableModal(null);
      fetchTables(selectedStore.storeId);
    } catch (err) {
      window.alert(err.response?.data?.message || '저장에 실패했습니다.');
    }
  };

  const handleDeleteTable = async () => {
    try {
      await deleteStoreTable(deleteTableTarget.tableId);
      setDeleteTableTarget(null);
      fetchTables(selectedStore.storeId);
    } catch (err) {
      window.alert(err.response?.data?.message || '삭제에 실패했습니다.');
    }
  };

  // 고객용 QR 주문 화면은 front-web(고객용 웹서비스)으로 이관되었으므로, 링크는 admin 사이트
  // (window.location.origin)가 아니라 front-web 주소로 생성한다. 환경변수 미설정 시 로컬
  // front-web 개발서버(3001)를 기본값으로 쓴다.
  const FRONT_WEB_BASE_URL = process.env.REACT_APP_FRONT_WEB_BASE_URL || 'http://localhost:3001';
  const getOrderUrl = (qrToken) => `${FRONT_WEB_BASE_URL}/order/${qrToken}`;

  const handleCopyLink = async (table) => {
    try {
      await navigator.clipboard.writeText(getOrderUrl(table.qrToken));
      setCopiedTableId(table.tableId);
      setTimeout(() => setCopiedTableId(null), 1500);
    } catch {
      window.prompt('아래 링크를 복사하세요:', getOrderUrl(table.qrToken));
    }
  };

  return (
    <AdminLayout title="매장관리" menuUrl="/admin/store">
      <div className="page-header">
        <div>
          <h1>매장관리</h1>
          <div className="subtitle">매장과 매장별 테이블을 등록합니다. 테이블마다 생성되는 QR 주문 링크로 고객이 직접 주문할 수 있습니다.</div>
        </div>
      </div>

      {error && <div className="card card-padded" style={{ marginBottom: 20 }}><span className="text-danger">{error}</span></div>}

      <div className="split-pane">
        <div className="card">
          <div className="flex-between" style={{ padding: '14px 16px', borderBottom: '1px solid var(--color-border-light)' }}>
            <strong style={{ fontSize: 13 }}>매장 목록</strong>
            {!isStoreAdmin && (
              <button className="btn btn-ghost btn-sm" onClick={() => setStoreModal('new')}>+ 추가</button>
            )}
          </div>
          <div className="pane-list">
            {loading && <div className="empty-row">불러오는 중...</div>}
            {!loading && stores.length === 0 && <div className="empty-row">등록된 매장이 없습니다.</div>}
            {!loading && stores.map((s) => (
              <div
                key={s.storeId}
                className={`pane-list-item${selectedStore?.storeId === s.storeId ? ' selected' : ''}`}
                onClick={() => setSelectedStore(s)}
              >
                <div>
                  {s.storeName}
                  {s.useYn === 'N' && <span style={{ marginLeft: 8 }}><StatusBadge value="N" /></span>}
                  {s.orderSettlementMode && s.orderSettlementMode !== 'PREPAID' && (
                    <span className="text-secondary" style={{ marginLeft: 8, fontSize: 11.5 }}>
                      ({SETTLEMENT_MODE_LABEL[s.orderSettlementMode] || s.orderSettlementMode})
                    </span>
                  )}
                </div>
                <div className="actions" onClick={(e) => e.stopPropagation()}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setStoreModal(s)}>수정</button>
                  {!isStoreAdmin && (
                    <button className="btn btn-danger btn-sm" onClick={() => setDeleteStoreTarget(s)}>삭제</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="flex-between" style={{ padding: '14px 16px', borderBottom: '1px solid var(--color-border-light)' }}>
            <strong style={{ fontSize: 13 }}>{selectedStore ? `${selectedStore.storeName} - 테이블 목록` : '테이블 목록'}</strong>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-secondary btn-sm" disabled={!selectedStore} onClick={() => setProductModalStore(selectedStore)}>취급상품 관리</button>
              <button className="btn btn-secondary btn-sm" disabled={!selectedStore} onClick={() => setReviewIncentiveModalStore(selectedStore)}>리뷰 유도 설정</button>
              <button className="btn btn-ghost btn-sm" disabled={!selectedStore} onClick={() => setTableModal('new')}>+ 테이블 추가</button>
            </div>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>테이블번호</th><th>상태</th><th>주문 링크(QR)</th><th>사용</th><th>관리</th></tr></thead>
              <tbody>
                {!selectedStore && <tr><td colSpan={5} className="empty-row">왼쪽에서 매장을 먼저 선택하세요.</td></tr>}
                {selectedStore && loadingTables && <tr><td colSpan={5} className="empty-row">불러오는 중...</td></tr>}
                {selectedStore && !loadingTables && tables.length === 0 && <tr><td colSpan={5} className="empty-row">등록된 테이블이 없습니다.</td></tr>}
                {selectedStore && !loadingTables && tables.map((t) => (
                  <tr key={t.tableId}>
                    <td>{t.tableNo}</td>
                    <td><StatusBadge value={t.tableStatus} label={t.tableStatus === 'IN_USE' ? '사용중' : '공석'} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => handleCopyLink(t)}>
                          {copiedTableId === t.tableId ? '복사됨!' : '링크 복사'}
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setQrDetailTable(t)}>QR 상세보기</button>
                      </div>
                    </td>
                    <td><StatusBadge value={t.useYn} /></td>
                    <td className="actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => setTableModal(t)}>수정</button>
                      <button className="btn btn-danger btn-sm" onClick={() => setDeleteTableTarget(t)}>삭제</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {storeModal && (
        <StoreFormModal store={storeModal === 'new' ? null : storeModal} onClose={() => setStoreModal(null)} onSave={handleSaveStore} />
      )}
      {tableModal && (
        <TableFormModal table={tableModal === 'new' ? null : tableModal} onClose={() => setTableModal(null)} onSave={handleSaveTable} />
      )}
      {productModalStore && (
        <StoreProductModal store={productModalStore} adminName={auth.adminName} onClose={() => setProductModalStore(null)} />
      )}
      {reviewIncentiveModalStore && (
        <StoreReviewIncentiveModal store={reviewIncentiveModalStore} adminName={auth.adminName} onClose={() => setReviewIncentiveModalStore(null)} />
      )}
      {qrDetailTable && (
        <QrDetailModal
          table={qrDetailTable}
          storeName={selectedStore?.storeName}
          orderUrl={getOrderUrl(qrDetailTable.qrToken)}
          onClose={() => setQrDetailTable(null)}
        />
      )}

      {deleteStoreTarget && (
        <ConfirmDialog
          title="매장 삭제"
          message={`'${deleteStoreTarget.storeName}' 매장을 삭제하시겠습니까?`}
          confirmLabel="삭제"
          danger
          onCancel={() => setDeleteStoreTarget(null)}
          onConfirm={handleDeleteStore}
        />
      )}
      {deleteTableTarget && (
        <ConfirmDialog
          title="테이블 삭제"
          message={`'${deleteTableTarget.tableNo}' 테이블을 삭제하시겠습니까? 이 테이블의 QR 링크는 더 이상 동작하지 않습니다.`}
          confirmLabel="삭제"
          danger
          onCancel={() => setDeleteTableTarget(null)}
          onConfirm={handleDeleteTable}
        />
      )}
    </AdminLayout>
  );
}

/**
 * 테이블 QR 주문 링크를 QR 코드 이미지로 크게 보여주는 모달.
 * 매장에 붙일 QR 코드를 바로 확인하고, PNG 로 내려받거나(인쇄용) 링크를 복사할 수 있다.
 * QR 은 클라이언트에서 qrcode.react 로 직접 렌더링하므로 외부 서비스로 URL 이 전송되지 않는다.
 *
 * 레퍼런스 벤치마킹(2026-07-28): 랜딩페이지의 "QR스티커 / 포스터" 다운로드 기능을
 * 벤치마킹했다. 기존에는 QR 코드 단독 이미지(흑백 사각형)만 다운로드할 수 있었는데,
 * 매장명·브랜딩 문구·안내 문구가 합성된 인쇄용 스티커/포스터 이미지를 캔버스로 직접
 * 합성해 다운로드할 수 있게 추가했다(별도 디자인 툴/서버 렌더링 없이 클라이언트에서 완결).
 */
function QrDetailModal({ table, storeName, orderUrl, onClose }) {
  const canvasWrapRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(orderUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt('아래 링크를 복사하세요:', orderUrl);
    }
  };

  const handleDownload = () => {
    const canvas = canvasWrapRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    const safeStore = (storeName || 'store').replace(/[^\w가-힣-]/g, '_');
    link.download = `QR_${safeStore}_table-${table.tableNo}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  /**
   * QR 코드에 매장명/브랜딩 문구/안내 문구를 합성해 인쇄용 스티커(작은 사이즈) 또는
   * 포스터(큰 사이즈) 이미지로 다운로드한다. 이미 렌더링된 QRCodeCanvas 를 그대로
   * drawImage 로 옮겨 붙이는 방식이라 QR 자체는 다시 생성하지 않는다.
   */
  const composeAndDownload = (kind) => {
    const qrCanvas = canvasWrapRef.current?.querySelector('canvas');
    if (!qrCanvas) return;

    const isPoster = kind === 'poster';
    const width = isPoster ? 800 : 480;
    const height = isPoster ? 1120 : 640;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    const font = (size, bold) => `${bold ? 'bold ' : ''}${size}px "Malgun Gothic", "맑은 고딕", sans-serif`;

    // 배경
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    // 상단 브랜드 배너
    const bandHeight = isPoster ? 220 : 150;
    const gradient = ctx.createLinearGradient(0, 0, width, 0);
    gradient.addColorStop(0, '#3B5BFF');
    gradient.addColorStop(1, '#22C55E');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, bandHeight);

    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.font = font(isPoster ? 52 : 30, true);
    ctx.fillText('QR 찍고 주문하기', width / 2, bandHeight / 2 - (isPoster ? 6 : 2));
    ctx.font = font(isPoster ? 24 : 14, false);
    ctx.fillText('혜택 확인하고 리뷰까지', width / 2, bandHeight / 2 + (isPoster ? 40 : 24));

    // QR 코드 (흰 박스 안에 스캔 여백 확보)
    const qrSize = isPoster ? 420 : 260;
    const qrX = (width - qrSize) / 2;
    const qrY = bandHeight + (isPoster ? 60 : 36);
    const pad = isPoster ? 24 : 14;
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 2;
    ctx.fillRect(qrX - pad, qrY - pad, qrSize + pad * 2, qrSize + pad * 2);
    ctx.strokeRect(qrX - pad, qrY - pad, qrSize + pad * 2, qrSize + pad * 2);
    ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

    // 매장명 / 테이블 번호
    ctx.fillStyle = '#1a1a1a';
    ctx.font = font(isPoster ? 32 : 18, true);
    const labelY = qrY + qrSize + pad + (isPoster ? 60 : 36);
    ctx.fillText(`${storeName || ''} · 테이블 ${table.tableNo}`, width / 2, labelY);

    // 안내 문구
    ctx.fillStyle = '#666666';
    ctx.font = font(isPoster ? 22 : 13, false);
    ctx.fillText('QR을 스캔하면 바로 메뉴 확인 및 주문이 가능합니다', width / 2, labelY + (isPoster ? 40 : 24));

    // 하단 브랜드 표기
    ctx.fillStyle = '#aaaaaa';
    ctx.font = font(isPoster ? 18 : 11, false);
    ctx.fillText('powered by onetak', width / 2, height - (isPoster ? 30 : 18));

    const link = document.createElement('a');
    const safeStore = (storeName || 'store').replace(/[^\w가-힣-]/g, '_');
    link.download = `${isPoster ? 'QR포스터' : 'QR스티커'}_${safeStore}_table-${table.tableNo}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <Modal
      title="QR 주문 링크 상세보기"
      onClose={onClose}
      width={360}
      footer={<button className="btn btn-secondary" onClick={onClose}>닫기</button>}
    >
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 2 }}>
          {storeName ? `${storeName} · ` : ''}테이블 {table.tableNo}
        </div>
        <div className="text-secondary" style={{ fontSize: 12, marginBottom: 16 }}>
          고객이 이 QR 을 스캔하면 해당 테이블의 주문 화면으로 연결됩니다.
        </div>

        <div
          ref={canvasWrapRef}
          style={{ display: 'inline-block', padding: 16, background: '#fff', border: '1px solid var(--color-border-light)', borderRadius: 'var(--radius-sm)' }}
        >
          {/* level=M(오류복원 15%), includeMargin 으로 스캔 여백 확보. 인쇄를 위해 256px 로 크게 렌더링. */}
          <QRCodeCanvas value={orderUrl} size={256} level="M" includeMargin marginSize={4} />
        </div>

        <div
          className="text-secondary"
          style={{ fontSize: 11.5, wordBreak: 'break-all', margin: '14px 0 16px', padding: '8px 10px', background: 'var(--color-bg-subtle, #f8f9fa)', borderRadius: 'var(--radius-sm)' }}
        >
          {orderUrl}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={handleCopy}>{copied ? '복사됨!' : '링크 복사'}</button>
          <button className="btn btn-secondary btn-sm" onClick={handleDownload}>QR 이미지 다운로드</button>
          <button className="btn btn-secondary btn-sm" onClick={() => composeAndDownload('sticker')}>QR 스티커 다운로드</button>
          <button className="btn btn-primary btn-sm" onClick={() => composeAndDownload('poster')}>QR 포스터 다운로드</button>
        </div>
      </div>
    </Modal>
  );
}

function StoreFormModal({ store, onClose, onSave }) {
  const isNew = !store;
  const [form, setForm] = useState({
    storeName: store?.storeName || '',
    foodtechStoreCode: store?.foodtechStoreCode || '',
    address: store?.address || '',
    phone: store?.phone || '',
    latitude: store?.latitude ?? '',
    longitude: store?.longitude ?? '',
    brandId: store?.brandId ?? '',
    useYn: store?.useYn || 'Y',
    orderSettlementMode: store?.orderSettlementMode || 'PREPAID',
  });
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    getBrandList()
      .then((res) => setBrands(res.data || []))
      .catch(() => setBrands([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        ...form,
        latitude: form.latitude === '' ? null : Number(form.latitude),
        longitude: form.longitude === '' ? null : Number(form.longitude),
        brandId: form.brandId === '' ? null : Number(form.brandId),
      });
    } finally {
      setSaving(false);
    }
  };

  /** 관리자가 매장 현장에서 이 화면을 연 경우, 브라우저 현재위치로 위도/경도를 바로 채울 수 있게 해준다. */
  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      window.alert('이 브라우저는 위치 조회를 지원하지 않습니다. 위도/경도를 직접 입력하세요.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, latitude: pos.coords.latitude.toFixed(7), longitude: pos.coords.longitude.toFixed(7) }));
        setLocating(false);
      },
      () => {
        window.alert('현재 위치를 가져오지 못했습니다. 위도/경도를 직접 입력하세요.');
        setLocating(false);
      }
    );
  };

  return (
    <Modal
      title={isNew ? '매장 등록' : '매장 수정'}
      onClose={onClose}
      width={400}
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>취소</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
      </>}
    >
      <form onSubmit={submit}>
        <div className="field">
          <label>매장명</label>
          <input className="input" value={form.storeName} onChange={(e) => setForm({ ...form, storeName: e.target.value })} required />
        </div>
        <div className="field">
          <label>푸드테크 매장코드 <span style={{ fontWeight: 400, color: 'var(--color-text-tertiary)' }}>(선택)</span></label>
          <input className="input" value={form.foodtechStoreCode} onChange={(e) => setForm({ ...form, foodtechStoreCode: e.target.value })} placeholder="외부 푸드테크 매장코드(입력 시 결제 후 연동 발송)" />
        </div>
        <div className="field">
          <label>주소</label>
          <input className="input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </div>
        <div className="field">
          <label>연락처</label>
          <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
        <div className="field">
          <label>위도 / 경도 <span className="text-secondary" style={{ fontWeight: 400 }}>(고객 앱의 "내 주변 매장" 반경 검색에 사용됩니다)</span></label>
          <div className="field-row">
            <input className="input" type="number" step="0.0000001" placeholder="위도 (예: 37.5012767)" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} />
            <input className="input" type="number" step="0.0000001" placeholder="경도 (예: 127.0396597)" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} />
            <button type="button" className="btn btn-secondary btn-sm" onClick={useCurrentLocation} disabled={locating} style={{ flexShrink: 0 }}>
              {locating ? '조회 중...' : '현재 위치'}
            </button>
          </div>
        </div>
        <div className="field">
          <label>브랜드 <span className="text-secondary" style={{ fontWeight: 400 }}>(선택 - QR오더 화면에 브랜드 소개가 노출됩니다)</span></label>
          <select className="input" value={form.brandId} onChange={(e) => setForm({ ...form, brandId: e.target.value })}>
            <option value="">브랜드 없음</option>
            {brands.map((b) => (
              <option key={b.brandId} value={b.brandId}>{b.brandName}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>
            주문 정산 방식{' '}
            <span className="text-secondary" style={{ fontWeight: 400 }}>
             
            </span>
          </label>
          <select
            className="input"
            value={form.orderSettlementMode}
            onChange={(e) => setForm({ ...form, orderSettlementMode: e.target.value })}
          >
            <option value="PREPAID">선결제 (기본 - 체크아웃에서 결제방식을 고르고 즉시 결제완료로 기록)</option>
            <option value="POSTPAID">후결제 (주문만 먼저 접수, 결제는 나중에 - 정산완료 전까지 결제대기)</option>
            <option value="ORDER_ONLY">주문전용 (결제방식 선택 없이 주문만 접수, 결제는 카운터/POS에서 처리)</option>
          </select>
        </div>
        <div className="field checkbox-row">
          <input type="checkbox" id="storeUseYn" checked={form.useYn === 'Y'} onChange={(e) => setForm({ ...form, useYn: e.target.checked ? 'Y' : 'N' })} />
          <label htmlFor="storeUseYn" style={{ margin: 0 }}>사용함 (끄면 테이블오더 접수가 중단됩니다)</label>
        </div>
      </form>
    </Modal>
  );
}

function TableFormModal({ table, onClose, onSave }) {
  const isNew = !table;
  const [form, setForm] = useState({
    tableNo: table?.tableNo || '',
    useYn: table?.useYn || 'Y',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isNew ? '테이블 추가' : '테이블 수정'}
      onClose={onClose}
      width={360}
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>취소</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
      </>}
    >
      <form onSubmit={submit}>
        <div className="field">
          <label>테이블번호</label>
          <input className="input" value={form.tableNo} onChange={(e) => setForm({ ...form, tableNo: e.target.value })} placeholder="예: 1, A-3" required />
        </div>
        <div className="field checkbox-row">
          <input type="checkbox" id="tableUseYn" checked={form.useYn === 'Y'} onChange={(e) => setForm({ ...form, useYn: e.target.checked ? 'Y' : 'N' })} />
          <label htmlFor="tableUseYn" style={{ margin: 0 }}>사용함</label>
        </div>
      </form>
    </Modal>
  );
}

/**
 * 매장별 취급상품 관리 모달.
 * 전체 상품 목록을 보여주고, 상품마다 "취급중/취급안함" 토글로 즉시 반영한다.
 * 제외된 상품은 해당 매장의 테이블오더 메뉴에서 노출되지 않고 주문도 거부된다.
 * 상품 행을 펼치면(▶) 상품별 옵션 목록이 나오고, 옵션 단위로도 취급 여부를 관리할 수 있다
 * (상품 자체는 취급하되 특정 옵션-사이즈/토핑 등-만 이 매장에서 빼고 싶은 경우).
 */
function StoreProductModal({ store, adminName, onClose }) {
  const [products, setProducts] = useState([]);
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [excludedOptionIds, setExcludedOptionIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null); // 처리 중인 상품 (연타 방지)
  const [savingOptionId, setSavingOptionId] = useState(null); // 처리 중인 옵션 (연타 방지)
  const [keyword, setKeyword] = useState('');

  const [expandedProductId, setExpandedProductId] = useState(null);
  const [optionsByProduct, setOptionsByProduct] = useState({}); // productId -> options[]
  const [loadingOptionsFor, setLoadingOptionsFor] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [productsRes, excludedRes, excludedOptionsRes] = await Promise.all([
        getProductList({ pageNo: 1, pageSize: 200 }),
        getExcludedProducts(store.storeId),
        getExcludedOptions(store.storeId),
      ]);
      setProducts(productsRes.data?.list || []);
      setExcludedIds(new Set(excludedRes.data || []));
      setExcludedOptionIds(new Set(excludedOptionsRes.data || []));
    } catch (err) {
      setError(err.response?.data?.message || '상품 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [store.storeId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleToggle = async (product) => {
    const isExcluded = excludedIds.has(product.productId);
    setSavingId(product.productId);
    try {
      if (isExcluded) {
        await includeProduct(store.storeId, product.productId);
        setExcludedIds((prev) => {
          const next = new Set(prev);
          next.delete(product.productId);
          return next;
        });
      } else {
        await excludeProduct(store.storeId, product.productId, adminName);
        setExcludedIds((prev) => new Set(prev).add(product.productId));
      }
    } catch (err) {
      window.alert(err.response?.data?.message || '처리에 실패했습니다.');
    } finally {
      setSavingId(null);
    }
  };

  const handleToggleExpand = async (product) => {
    if (expandedProductId === product.productId) {
      setExpandedProductId(null);
      return;
    }
    setExpandedProductId(product.productId);
    if (!optionsByProduct[product.productId]) {
      setLoadingOptionsFor(product.productId);
      try {
        const res = await getProduct(product.productId);
        setOptionsByProduct((prev) => ({ ...prev, [product.productId]: res.data?.options || [] }));
      } catch (err) {
        window.alert(err.response?.data?.message || '옵션 목록을 불러오지 못했습니다.');
        setExpandedProductId(null);
      } finally {
        setLoadingOptionsFor(null);
      }
    }
  };

  const handleToggleOption = async (option) => {
    const isExcluded = excludedOptionIds.has(option.optionId);
    setSavingOptionId(option.optionId);
    try {
      if (isExcluded) {
        await includeOption(store.storeId, option.optionId);
        setExcludedOptionIds((prev) => {
          const next = new Set(prev);
          next.delete(option.optionId);
          return next;
        });
      } else {
        await excludeOption(store.storeId, option.optionId, adminName);
        setExcludedOptionIds((prev) => new Set(prev).add(option.optionId));
      }
    } catch (err) {
      window.alert(err.response?.data?.message || '처리에 실패했습니다.');
    } finally {
      setSavingOptionId(null);
    }
  };

  const filtered = products.filter((p) => !keyword || p.productName.includes(keyword));
  const excludedCount = excludedIds.size;
  const excludedOptionCount = excludedOptionIds.size;

  return (
    <Modal
      title={`${store.storeName} - 취급상품 관리`}
      onClose={onClose}
      width={600}
      footer={<button className="btn btn-secondary" onClick={onClose}>닫기</button>}
    >
      <div className="text-secondary" style={{ fontSize: 12.5, marginBottom: 12 }}>
        취급 안함으로 바꾸면 이 매장의 테이블오더 메뉴에서 즉시 제외됩니다. 상품명 왼쪽의 화살표를 누르면
        옵션별로도 취급 여부를 따로 관리할 수 있습니다 (상품은 취급하되 특정 옵션만 제외).
        {excludedCount > 0 && <> 현재 <strong style={{ color: 'var(--color-accent)' }}>{excludedCount}개</strong> 상품 제외 중.</>}
        {excludedOptionCount > 0 && <> <strong style={{ color: 'var(--color-accent)' }}>{excludedOptionCount}개</strong> 옵션 제외 중.</>}
      </div>

      <input
        className="input"
        placeholder="상품명 검색"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        style={{ marginBottom: 12 }}
      />

      {loading && <div className="empty-row">불러오는 중...</div>}
      {!loading && error && <div className="empty-row error-state">{error}</div>}
      {!loading && !error && filtered.length === 0 && <div className="empty-row">상품이 없습니다.</div>}

      {!loading && !error && filtered.length > 0 && (
        <div style={{ maxHeight: 440, overflowY: 'auto', border: '1px solid var(--color-border-light)', borderRadius: 'var(--radius-sm)' }}>
          {filtered.map((p) => {
            const isExcluded = excludedIds.has(p.productId);
            const isExpanded = expandedProductId === p.productId;
            const options = optionsByProduct[p.productId];
            return (
              <div key={p.productId} style={{ borderBottom: '1px solid var(--color-border-light)' }}>
                <div className="flex-between" style={{ padding: '10px 14px' }}>
                  <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ padding: '2px 6px', flexShrink: 0 }}
                      onClick={() => handleToggleExpand(p)}
                      title="옵션별 취급 관리"
                    >
                      {isExpanded ? '▼' : '▶'}
                    </button>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isExcluded ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)', textDecoration: isExcluded ? 'line-through' : 'none' }}>
                        {p.productName}
                      </div>
                      <div className="text-secondary" style={{ fontSize: 12 }}>{Number(p.basePrice).toLocaleString()}원</div>
                    </div>
                  </div>
                  <button
                    className={`btn btn-sm ${isExcluded ? 'btn-danger' : 'btn-secondary'}`}
                    disabled={savingId === p.productId}
                    onClick={() => handleToggle(p)}
                    style={{ flexShrink: 0 }}
                  >
                    {savingId === p.productId ? '처리 중...' : (isExcluded ? '취급 안함' : '취급중')}
                  </button>
                </div>

                {isExpanded && (
                  <div style={{ background: 'var(--color-bg-subtle, #f8f9fa)', padding: '4px 14px 8px 42px' }}>
                    {loadingOptionsFor === p.productId && <div className="text-secondary" style={{ fontSize: 12, padding: '6px 0' }}>옵션 불러오는 중...</div>}
                    {loadingOptionsFor !== p.productId && (!options || options.length === 0) && (
                      <div className="text-secondary" style={{ fontSize: 12, padding: '6px 0' }}>등록된 옵션이 없습니다.</div>
                    )}
                    {loadingOptionsFor !== p.productId && options && options.length > 0 && options.map((o) => {
                      const isOptionExcluded = excludedOptionIds.has(o.optionId);
                      return (
                        <div key={o.optionId} className="flex-between" style={{ padding: '6px 0', borderBottom: '1px dashed var(--color-border-light)' }}>
                          <div style={{ fontSize: 12.5, color: isOptionExcluded ? 'var(--color-text-tertiary)' : 'var(--color-text-secondary)', textDecoration: isOptionExcluded ? 'line-through' : 'none' }}>
                            {o.optionName}
                            {Number(o.additionalPrice) > 0 && <span style={{ marginLeft: 6, fontSize: 11.5 }}>(+{Number(o.additionalPrice).toLocaleString()}원)</span>}
                          </div>
                          <button
                            className={`btn btn-sm ${isOptionExcluded ? 'btn-danger' : 'btn-secondary'}`}
                            disabled={savingOptionId === o.optionId}
                            onClick={() => handleToggleOption(o)}
                            style={{ flexShrink: 0, padding: '2px 10px', fontSize: 11.5 }}
                          >
                            {savingOptionId === o.optionId ? '처리 중...' : (isOptionExcluded ? '취급 안함' : '취급중')}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

/**
 * 매장별 리뷰 유도(보상 안내) 설정 모달. 레퍼런스 의 "영수증 인증 없이 간편리뷰 +
 * 보상 안내" 기능을 벤치마킹 - 사용여부와 문구(예: "리뷰 쓰고 콜라 1캔 받아가세요")를
 * 매장 단위로 켜고 끌 수 있다. 켜두면 front-web QR오더 화면의 리뷰 작성 영역 상단에
 * 배너로 노출된다.
 */
function StoreReviewIncentiveModal({ store, adminName, onClose }) {
  const [form, setForm] = useState({ useYn: 'N', incentiveMessage: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getStoreReviewIncentive(store.storeId)
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        setForm({ useYn: data.useYn || 'N', incentiveMessage: data.incentiveMessage || '' });
      })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.message || '설정을 불러오지 못했습니다.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [store.storeId]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.useYn === 'Y' && !form.incentiveMessage.trim()) {
      window.alert('사용함으로 켜려면 유도 문구를 입력하세요.');
      return;
    }
    setSaving(true);
    try {
      await saveStoreReviewIncentive(store.storeId, form, adminName);
      onClose();
    } catch (err) {
      window.alert(err.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`${store.storeName} - 리뷰 유도 설정`}
      onClose={onClose}
      width={420}
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>취소</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving || loading}>{saving ? '저장 중...' : '저장'}</button>
      </>}
    >
      <div className="text-secondary" style={{ fontSize: 12.5, marginBottom: 12 }}>
        켜두면 고객이 QR오더 화면에서 리뷰를 쓸 때 "영수증 인증 없이 간편하게!" 문구와 함께
        아래에 입력한 보상 안내가 배너로 노출됩니다. (예: 리뷰 쓰고 콜라 1캔 받아가세요)
      </div>
      {loading && <div className="empty-row">불러오는 중...</div>}
      {!loading && error && <div className="empty-row error-state">{error}</div>}
      {!loading && !error && (
        <form onSubmit={submit}>
          <div className="field checkbox-row">
            <input
              type="checkbox"
              id="reviewIncentiveUseYn"
              checked={form.useYn === 'Y'}
              onChange={(e) => setForm({ ...form, useYn: e.target.checked ? 'Y' : 'N' })}
            />
            <label htmlFor="reviewIncentiveUseYn" style={{ margin: 0 }}>리뷰 유도 배너 사용함</label>
          </div>
          <div className="field">
            <label>유도 문구</label>
            <input
              className="input"
              value={form.incentiveMessage}
              onChange={(e) => setForm({ ...form, incentiveMessage: e.target.value })}
              placeholder="예: 리뷰 쓰고 콜라 1캔 받아가세요"
              maxLength={200}
            />
          </div>
        </form>
      )}
    </Modal>
  );
}
